import { useMemo, useRef, useEffect, useState } from 'react';
import { useThree, useFrame } from '@react-three/fiber';
import * as THREE from 'three';
import { TILE_SIZE, FLOOR_HEIGHT, WORLD_THEME_COLORS } from './constants';
import TileRenderer3D from './TileRenderer3D';
import type { TileType } from './TileRenderer3D';
import Character3DWebGL from './Character3DWebGL';
import Collectible3D from './Collectible3D';
import Enemy3DWebGL from './Enemy3DWebGL';
import HintPath3D from './HintPath3D';
import WarpOverlay3D from './WarpOverlay3D';
import FloatingText3D from './FloatingText3D';
import ParticleSystem3D from './ParticleSystem3D';
import type { GameState, Position } from '@/types/game';
import type { GraphicsQuality } from './GameBoard3D';
import { puzzles } from '@/game/puzzles';
import type { PuzzleData } from '@/types/puzzle';
import { getPuzzleGrid } from '@/types/puzzle';
import { getWorldForLevel } from '@/config/worlds';

interface BoardSceneProps {
  gameState: GameState;
  equippedCharacterId?: string;
  equippedProps?: string[];
  hintPath?: Position[] | null;
  hintPathHazards?: Set<string>;
  onCellClick?: (position: Position) => void;
  graphics?: GraphicsQuality;
}

// Map grid characters to tile types
function charToTileType(c: string): TileType {
  switch (c.toLowerCase()) {
    case 'x': return 'rock';
    case 'l': return 'lava';
    case 's': return 'stop';
    case 't': return 'teleporter';
    case 'o': return 'start';
    case 'e': return 'end';
    case 'p': return 'powerup';
    default: return 'empty';
  }
}

type CharacterGameState = 'entering' | 'playing' | 'won' | 'lost' | 'exiting';

// Deep platform that extends downward into the void
function PlatformBase({ cols, rows, groundColor, accentColor }: { cols: number; rows: number; groundColor: string; accentColor: string }) {
  const width = cols * TILE_SIZE + 0.5;
  const depth = rows * TILE_SIZE + 0.5;
  const platformHeight = 20;
  const topY = FLOOR_HEIGHT - 0.01;

  const sideColor = useMemo(() => {
    return new THREE.Color(groundColor).multiplyScalar(0.35).getStyle();
  }, [groundColor]);

  const bottomColor = useMemo(() => {
    return new THREE.Color(groundColor).multiplyScalar(0.12).getStyle();
  }, [groundColor]);

  const rimColor = useMemo(() => {
    // Blend accent with dark for a themed rim
    const c = new THREE.Color(accentColor);
    c.multiplyScalar(0.5);
    return c.getStyle();
  }, [accentColor]);

  const rimHighlight = useMemo(() => {
    const c = new THREE.Color(accentColor);
    c.multiplyScalar(0.7);
    return c.getStyle();
  }, [accentColor]);

  return (
    <group>
      {/* Top surface - base under tiles (slightly below floor level to avoid z-fighting) */}
      <mesh position={[0, topY - 0.06, 0]} receiveShadow>
        <boxGeometry args={[width, 0.08, depth]} />
        <meshStandardMaterial color={groundColor} roughness={0.8} metalness={0.05} />
      </mesh>

      {/* Deep column beneath */}
      <mesh position={[0, topY - platformHeight / 2, 0]}>
        <boxGeometry args={[width, platformHeight, depth]} />
        <meshStandardMaterial color={sideColor} roughness={0.95} metalness={0} />
      </mesh>

      {/* Themed rim - top highlight strip */}
      {[
        { pos: [0, topY + 0.06, depth / 2 + 0.02] as const, size: [width + 0.06, 0.06, 0.04] as const },
        { pos: [0, topY + 0.06, -depth / 2 - 0.02] as const, size: [width + 0.06, 0.06, 0.04] as const },
        { pos: [-width / 2 - 0.02, topY + 0.06, 0] as const, size: [0.04, 0.06, depth + 0.06] as const },
        { pos: [width / 2 + 0.02, topY + 0.06, 0] as const, size: [0.04, 0.06, depth + 0.06] as const },
      ].map((edge, i) => (
        <mesh key={`rim-top-${i}`} position={[edge.pos[0], edge.pos[1], edge.pos[2]]}>
          <boxGeometry args={[edge.size[0], edge.size[1], edge.size[2]]} />
          <meshStandardMaterial
            color={rimHighlight}
            emissive={accentColor}
            emissiveIntensity={0.15}
            roughness={0.4}
            metalness={0.2}
          />
        </mesh>
      ))}

      {/* Rim - lower body strip */}
      {[
        { pos: [0, topY - 0.02, depth / 2 + 0.02] as const, size: [width + 0.06, 0.12, 0.05] as const },
        { pos: [0, topY - 0.02, -depth / 2 - 0.02] as const, size: [width + 0.06, 0.12, 0.05] as const },
        { pos: [-width / 2 - 0.02, topY - 0.02, 0] as const, size: [0.05, 0.12, depth + 0.06] as const },
        { pos: [width / 2 + 0.02, topY - 0.02, 0] as const, size: [0.05, 0.12, depth + 0.06] as const },
      ].map((edge, i) => (
        <mesh key={`rim-body-${i}`} position={[edge.pos[0], edge.pos[1], edge.pos[2]]}>
          <boxGeometry args={[edge.size[0], edge.size[1], edge.size[2]]} />
          <meshStandardMaterial color={rimColor} roughness={0.7} metalness={0.1} />
        </mesh>
      ))}

      {/* Corner accents - small glowing dots at corners */}
      {[
        [-width / 2 - 0.02, topY + 0.08, depth / 2 + 0.02],
        [width / 2 + 0.02, topY + 0.08, depth / 2 + 0.02],
        [-width / 2 - 0.02, topY + 0.08, -depth / 2 - 0.02],
        [width / 2 + 0.02, topY + 0.08, -depth / 2 - 0.02],
      ].map((pos, i) => (
        <mesh key={`corner-${i}`} position={[pos[0], pos[1], pos[2]]}>
          <sphereGeometry args={[0.04, 8, 8]} />
          <meshStandardMaterial
            color={accentColor}
            emissive={accentColor}
            emissiveIntensity={0.4}
            roughness={0.3}
          />
        </mesh>
      ))}

      {/* Void floor */}
      <mesh position={[0, topY - platformHeight, 0]} rotation={[-Math.PI / 2, 0, 0]}>
        <planeGeometry args={[width * 3, depth * 3]} />
        <meshBasicMaterial color={bottomColor} />
      </mesh>
    </group>
  );
}

// Ambient atmosphere: firefly-like orbs using instanced mesh for performance
function AmbientParticles({ theme, cols, rows, count = 30 }: { theme: string; cols: number; rows: number; count?: number }) {
  const meshRef = useRef<THREE.InstancedMesh>(null);
  const spread = Math.max(cols, rows) * TILE_SIZE * 1.8;
  const themeColors = WORLD_THEME_COLORS[theme] || WORLD_THEME_COLORS.greenwood;
  const dummy = useMemo(() => new THREE.Object3D(), []);

  const particles = useMemo(() => {
    return Array.from({ length: count }, (_, i) => {
      const isSpark = i < 20;
      return {
        x: (Math.random() - 0.5) * spread * (isSpark ? 1 : 0.8),
        y: (isSpark ? 0.3 : 0.5) + Math.random() * spread * (isSpark ? 0.5 : 0.35),
        z: (Math.random() - 0.5) * spread * (isSpark ? 1 : 0.8),
        size: isSpark ? 0.01 + Math.random() * 0.015 : 0.03 + Math.random() * 0.05,
        speed: isSpark ? 0.3 + Math.random() * 0.5 : 0.05 + Math.random() * 0.1,
        phase: Math.random() * Math.PI * 2,
        drift: isSpark ? 0.15 + Math.random() * 0.25 : 0.4 + Math.random() * 0.6,
        isSpark,
      };
    });
  }, [spread]);

  useFrame(({ clock }) => {
    if (!meshRef.current) return;
    const t = clock.getElapsedTime();

    particles.forEach((p, i) => {
      let px: number, py: number, pz: number, s: number;
      if (p.isSpark) {
        py = p.y + Math.sin(t * p.speed * 3 + p.phase) * p.drift;
        px = p.x + Math.sin(t * p.speed * 2 + p.phase * 1.3) * p.drift * 0.8;
        pz = p.z + Math.cos(t * p.speed * 1.7 + p.phase * 0.7) * p.drift * 0.6;
        const twinkle = Math.pow(Math.sin(t * p.speed * 5 + p.phase) * 0.5 + 0.5, 3);
        s = p.size * (0.5 + twinkle * 0.5);
      } else {
        py = p.y + Math.sin(t * p.speed + p.phase) * p.drift;
        px = p.x + Math.sin(t * p.speed * 0.6 + p.phase * 2) * p.drift * 0.4;
        pz = p.z + Math.cos(t * p.speed * 0.4 + p.phase) * p.drift * 0.3;
        s = p.size * (0.8 + Math.sin(t * p.speed * 2 + p.phase) * 0.2);
      }
      dummy.position.set(px, py, pz);
      dummy.scale.setScalar(s);
      dummy.updateMatrix();
      meshRef.current!.setMatrixAt(i, dummy.matrix);
    });
    meshRef.current.instanceMatrix.needsUpdate = true;
  });

  return (
    <instancedMesh ref={meshRef} args={[undefined, undefined, count]}>
      <sphereGeometry args={[1, 6, 6]} />
      <meshBasicMaterial color={themeColors.ambient} transparent opacity={0.3} />
    </instancedMesh>
  );
}

export default function BoardScene({ gameState, equippedCharacterId = 'default_hero', equippedProps = [], hintPath, hintPathHazards, onCellClick, graphics = 'high' }: BoardSceneProps) {
  const { camera, size, scene } = useThree();
  const cameraBasePos = useRef(new THREE.Vector3());
  const [characterState, setCharacterState] = useState<CharacterGameState>('entering');
  const prevPositionRef = useRef<Position>(gameState.position);
  const [isMoving, setIsMoving] = useState(false);
  const [moveDirection, setMoveDirection] = useState<'up' | 'down' | 'left' | 'right' | null>(null);
  const moveTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Get world theme
  const worldTheme = useMemo(() => {
    const world = getWorldForLevel(gameState.level);
    return world.id;
  }, [gameState.level]);

  const themeColors = WORLD_THEME_COLORS[worldTheme] || WORLD_THEME_COLORS.greenwood;

  // Parse grid
  const { tiles, totalCols, totalRows } = useMemo(() => {
    const isInfinite = gameState.gameMode === 'endless' && gameState.infiniteGrid;
    const puzzle = isInfinite ? null : (puzzles[gameState.level] as PuzzleData);
    if (!puzzle && !isInfinite) return { tiles: [], totalCols: 0, totalRows: 0 };

    const puzzleGrid = isInfinite ? gameState.infiniteGrid! : getPuzzleGrid(puzzle!);
    const rows = puzzleGrid.length;
    const cols = puzzleGrid[0]?.length || 0;

    const tileData: Array<{ type: TileType; col: number; row: number; isCheckerDark: boolean }> = [];

    puzzleGrid.forEach((rowStr, rowIndex) => {
      const cells = rowStr.split('');
      cells.forEach((cell, colIndex) => {
        tileData.push({
          type: charToTileType(cell),
          col: colIndex,
          row: rowIndex,
          isCheckerDark: (colIndex + rowIndex) % 2 === 1,
        });
      });
    });

    return { tiles: tileData, totalCols: cols, totalRows: rows };
  }, [gameState.level, gameState.gameMode, gameState.infiniteGrid]);

  // Detect movement
  useEffect(() => {
    const prev = prevPositionRef.current;
    const curr = gameState.position;
    const dx = curr[0] - prev[0];
    const dy = curr[1] - prev[1];

    if (dx !== 0 || dy !== 0) {
      const dir = Math.abs(dx) > Math.abs(dy)
        ? (dx > 0 ? 'right' : 'left')
        : (dy > 0 ? 'down' : 'up');
      setMoveDirection(dir as 'up' | 'down' | 'left' | 'right');
      setIsMoving(true);
      // Clear previous timer so rapid inputs don't cause flicker
      if (moveTimerRef.current) clearTimeout(moveTimerRef.current);
      moveTimerRef.current = setTimeout(() => {
        moveTimerRef.current = null;
        setIsMoving(false);
      }, 300);
    }
    prevPositionRef.current = curr;
  }, [gameState.position]);

  // Character state management - re-enter on level change, wave change, or game restart
  // Track both level AND infinite wave so character re-enters on wave transitions
  const infiniteWave = gameState.infiniteState?.wave ?? 0;
  useEffect(() => {
    setCharacterState('entering');
    const timer = setTimeout(() => setCharacterState('playing'), 1200);
    return () => clearTimeout(timer);
  }, [gameState.level, infiniteWave]);

  // Detect restart (position resets to start while character was lost)
  useEffect(() => {
    if (gameState.initialPosition) {
      const posKey = `${gameState.position[0]},${gameState.position[1]}`;
      const startKey = `${gameState.initialPosition[0]},${gameState.initialPosition[1]}`;
      if (posKey === startKey && characterState === 'lost') {
        setCharacterState('entering');
        const timer = setTimeout(() => setCharacterState('playing'), 1200);
        return () => clearTimeout(timer);
      }
    }
  }, [gameState.position, gameState.initialPosition, characterState]);

  // Listen for game events
  useEffect(() => {
    const handleGameOver = () => setCharacterState('lost');
    const handleLifeLost = () => setCharacterState('lost');

    window.addEventListener('gameOver', handleGameOver);
    window.addEventListener('lifeLost', handleLifeLost);
    return () => {
      window.removeEventListener('gameOver', handleGameOver);
      window.removeEventListener('lifeLost', handleLifeLost);
    };
  }, []);

  // Detect when player reaches end tile
  const prevIsPlaying = useRef(gameState.isPlaying);
  useEffect(() => {
    if (prevIsPlaying.current && !gameState.isPlaying && gameState.end) {
      const atEnd = gameState.position[0] === gameState.end[0] &&
        gameState.position[1] === gameState.end[1];
      if (atEnd) {
        setCharacterState('won');
        const exitTimer = setTimeout(() => setCharacterState('exiting'), 1000);
        return () => clearTimeout(exitTimer);
      }
    }
    prevIsPlaying.current = gameState.isPlaying;
  }, [gameState.isPlaying, gameState.position, gameState.end]);

  // Set scene background and fog
  useEffect(() => {
    scene.background = new THREE.Color(themeColors.sky);
    scene.fog = new THREE.FogExp2(themeColors.fog, 0.035);
  }, [scene, themeColors]);

  // Camera auto-fit
  useEffect(() => {
    if (totalCols === 0 || totalRows === 0) return;
    const cam = camera as THREE.PerspectiveCamera;

    const boardWidth = totalCols * TILE_SIZE;
    const boardDepth = totalRows * TILE_SIZE;

    // Steep top-down angle so walls never obscure the character
    const elevation = Math.PI * 0.42;
    const fovRad = THREE.MathUtils.degToRad(cam.fov);
    const aspect = size.width / size.height;

    const projectedHeight = boardDepth * Math.cos(elevation) + 1.5;
    const projectedWidth = boardWidth;

    const distForHeight = (projectedHeight / 2) / Math.tan(fovRad / 2);
    const hFovRad = 2 * Math.atan(Math.tan(fovRad / 2) * aspect);
    const distForWidth = (projectedWidth / 2) / Math.tan(hFovRad / 2);

    // On wide/desktop screens, pull the camera back further to prevent oversized boards
    const aspectPadding = aspect > 1.5 ? 1.4 + (aspect - 1.5) * 0.15 : 1.25;
    const distance = Math.max(distForHeight, distForWidth) * aspectPadding;

    const cameraY = distance * Math.sin(elevation);
    const cameraZ = distance * Math.cos(elevation);

    cam.position.set(0, cameraY, cameraZ);
    cam.lookAt(0, 0, 0);
    cameraBasePos.current.set(0, cameraY, cameraZ);
  }, [totalCols, totalRows, camera, size.width, size.height]);

  // Subtle camera sway following the player loosely
  const playerWorldPos = useRef(new THREE.Vector3());
  const cameraOffset = useRef(new THREE.Vector3());
  useFrame((_, delta) => {
    if (totalCols === 0 || totalRows === 0) return;

    // Compute player world position
    const px = (gameState.position[0] - (totalCols - 1) / 2) * TILE_SIZE;
    const pz = (gameState.position[1] - (totalRows - 1) / 2) * TILE_SIZE;
    playerWorldPos.current.set(px, 0, pz);

    // Very gentle offset toward player (max ~0.3 units)
    const targetOffsetX = px * 0.08;
    const targetOffsetZ = pz * 0.05;
    cameraOffset.current.x += (targetOffsetX - cameraOffset.current.x) * delta * 1.5;
    cameraOffset.current.z += (targetOffsetZ - cameraOffset.current.z) * delta * 1.5;
  });

  // Filter uncollected collectibles
  const uncollectedCollectibles = useMemo(() => {
    return (gameState.collectibles || []).filter(c => !c.isCollected);
  }, [gameState.collectibles]);

  return (
    <>
      {/* Lighting setup matching showcase quality */}
      <ambientLight intensity={0.4} color={themeColors.ambient} />

      {/* Main key light - high up, strong shadows */}
      <directionalLight
        position={[10, 20, 10]}
        intensity={1.5}
        castShadow={graphics !== 'low'}
        shadow-mapSize={graphics === 'high' ? [1024, 1024] : [512, 512]}
        shadow-camera-left={-12}
        shadow-camera-right={12}
        shadow-camera-top={12}
        shadow-camera-bottom={-12}
        shadow-bias={-0.001}
      />

      {/* Cool-toned fill spotlight from opposite side */}
      <spotLight
        position={[-10, 20, -10]}
        intensity={2}
        color="#4a90e2"
        penumbra={1}
      />

      {/* Hemisphere for natural sky/ground bounce */}
      <hemisphereLight
        color={themeColors.ambient}
        groundColor={themeColors.ground}
        intensity={0.5}
      />

      {/* Warm point light centered above the board for glow */}
      <pointLight
        position={[0, 6, 0]}
        color={themeColors.ambient}
        intensity={0.8}
        distance={25}
        decay={2}
      />

      {/* Deep platform */}
      {totalCols > 0 && totalRows > 0 && (
        <PlatformBase cols={totalCols} rows={totalRows} groundColor={themeColors.ground} accentColor={themeColors.ambient} />
      )}

      {/* Ambient particles */}
      {totalCols > 0 && graphics !== 'low' && (
        <AmbientParticles theme={worldTheme} cols={totalCols} rows={totalRows} count={graphics === 'high' ? 30 : 15} />
      )}

      {/* Tiles */}
      {tiles.map((tile) => (
        <TileRenderer3D
          key={`${tile.col}-${tile.row}`}
          type={tile.type}
          col={tile.col}
          row={tile.row}
          totalCols={totalCols}
          totalRows={totalRows}
          isCheckerDark={tile.isCheckerDark}
          graphics={graphics}
        />
      ))}

      {/* Character */}
      {totalCols > 0 && (
        <Character3DWebGL
          position={gameState.position}
          direction={moveDirection}
          hasCollided={!!gameState.hasCollided}
          isMoving={isMoving}
          characterId={equippedCharacterId}
          totalCols={totalCols}
          totalRows={totalRows}
          gameState={characterState}
          equippedProps={equippedProps}
        />
      )}

      {/* Collectibles */}
      {uncollectedCollectibles.map((collectible) => (
        <Collectible3D
          key={collectible.id}
          position={collectible.position}
          type={collectible.type}
          totalCols={totalCols}
          totalRows={totalRows}
        />
      ))}

      {/* Enemies */}
      {(gameState.enemies || []).map((enemy) => (
        <Enemy3DWebGL
          key={enemy.id}
          enemy={enemy}
          totalCols={totalCols}
          totalRows={totalRows}
          isFrozen={gameState.frozenTurns > 0}
        />
      ))}

      {/* Hint path */}
      {hintPath && hintPath.length > 0 && (
        <HintPath3D
          path={hintPath}
          hazards={hintPathHazards}
          totalCols={totalCols}
          totalRows={totalRows}
        />
      )}

      {/* Warp overlay */}
      {gameState.isWarpSelecting && onCellClick && (
        <WarpOverlay3D
          gameState={gameState}
          totalCols={totalCols}
          totalRows={totalRows}
          onTileClick={onCellClick}
        />
      )}

      {/* Floating text */}
      <FloatingText3D totalCols={totalCols} totalRows={totalRows} />

      {/* Particle effects */}
      <ParticleSystem3D
        totalCols={totalCols}
        totalRows={totalRows}
        playerPosition={gameState.position}
      />
    </>
  );
}
